import { useMutation } from "@tanstack/react-query";
import {
  DMModal,
  DMModalContent,
  DMModalContentWrapper,
  DMModalFooter,
  DMModalTitle,
} from "../../../../shared/DMModal";
import Button from "../../../../student/components/generic/button";
import {
  ManagerForm,
  ManagerFormNumberField,
  ManagerFormSelectField,
  ManagerFormTextField,
} from "../../form/ManagerForm";
import { executeQuery } from "../../../../utils";
import { useEffect, useState } from "react";
import { LearnerLicense } from "./types";
import { format } from "date-fns";
import { useDataTable } from "../../DataTable/DataTableContext";
import { useFormContext } from "react-hook-form";

const SubmitButton: React.FC<{ isLoading?: boolean }> = ({ isLoading }) => {
  const {
    formState: { isValid },
  } = useFormContext();
  return (
    <Button isLoading={isLoading} disabled={!isValid}>
      Save License
    </Button>
  );
};

export const EditVoucherLicenseModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  license?: LearnerLicense;
  onSave: () => void;
}> = ({ visible, onClose, onSave, license }) => {
  const isEditing = !!license;

  const [nonFieldError, setNonFieldError] = useState<string | null>(null);

  const { learnerLookup } = useDataTable<{
    learnerLookup: Record<
      string,
      { first: string; last: string; email: string }
    >;
  }>();
  const learnerDetails = license?.acceptedBy
    ? learnerLookup[license.acceptedBy]
    : undefined;
  const learnerStr = learnerDetails
    ? `${learnerDetails.first} ${learnerDetails.last} (${learnerDetails.email})`
    : license?.acceptedBy;
  const acceptedAt = new Date(license?.acceptedAt ?? "");

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: LearnerLicense) => {
      setNonFieldError(null);
      return executeQuery({
        path: isEditing
          ? `/payments/learner-licenses/edit/${license._id}`
          : "/payments/learner-licenses/create",
        method: "post",
        requestBody: { license: data },
      });
    },
  });

  const onSubmit = (data: LearnerLicense) => {
    mutate(data, {
      onSuccess: () => onSave(),
      onError: (error: any) => {
        if (error.error) {
          setNonFieldError(error.error);
        } else {
          setNonFieldError("An unexpected error occurred. Please try again.");
        }
      },
    });
  };

  useEffect(() => {
    if (!visible) {
      setNonFieldError(null);
    }
  }, [visible]);

  return (
    <DMModal visible={visible} onClose={onClose}>
      <ManagerForm onSubmit={onSubmit} defaultValues={license}>
        <DMModalContentWrapper>
          <DMModalTitle>
            {isEditing
              ? "Edit Voucher License"
              : "Create a New Voucher License"}
          </DMModalTitle>
          <DMModalContent className="gap-8">
            {nonFieldError && (
              <p className="text-dm-error-500">{nonFieldError}</p>
            )}
            <ManagerFormTextField
              fieldName="contactEmail"
              label="Contact email"
              type="email"
              helpText={
                isEditing ? (
                  <>
                    Updating the contact email <em>will not</em> send a new
                    invite link.
                  </>
                ) : (
                  "This is used to send the invite link. It doesn't have to be the person who will use the license."
                )
              }
              required
            />

            <div className="flex w-full gap-6">
              <ManagerFormTextField
                fieldName="contactFirst"
                label="Contact first name"
                options={{ setValueAs: (value) => value || undefined }}
              />
              <ManagerFormTextField
                fieldName="contactLast"
                label="Contact last name"
                options={{ setValueAs: (value) => value || undefined }}
              />
            </div>

            <div className="flex w-full gap-6">
              <ManagerFormNumberField
                fieldName="learnerQuantity"
                label="Learner quantity"
                helpText="The number of learners who will use this license. For independent learners, choose 1."
                min={1}
                max={100}
                disabled={isEditing}
                required
              />
              <ManagerFormSelectField
                fieldName="licenseLength"
                label="Amount of time"
                helpText="The time will start when the user accepts the invite link."
                options={{
                  valueAsNumber: true,
                }}
                choices={[
                  { value: "", label: "Select length" },
                  { value: "12", label: "12 months" },
                ]}
                disabled={isEditing}
                required
              />
            </div>

            <div className="flex w-full gap-6">
              <ManagerFormTextField
                fieldName="quoteNumber"
                label="Quote or invoice number"
                options={{ setValueAs: (value) => value || undefined }}
              />
              <ManagerFormTextField
                fieldName="voucherNumber"
                label="Voucher number"
                options={{ setValueAs: (value) => value || undefined }}
              />
            </div>

            {isEditing && (
              <ManagerFormTextField
                fieldName="accessCode"
                label="Access code"
                disabled
              />
            )}

            {license?.acceptedAt && license.acceptedBy && (
              <div className="flex w-full gap-4 border-t border-dm-charcoal-100 pt-8 text-sm">
                <p className="flex flex-1 flex-col gap-1">
                  <strong>Accepted by</strong>
                  <span>{learnerStr}</span>
                </p>
                <p className="flex flex-col gap-1">
                  <strong>Accepted on</strong>
                  <span>{format(acceptedAt, "PPPp")}</span>
                </p>
              </div>
            )}
          </DMModalContent>
          <DMModalFooter>
            <Button type="outline" submit={false} onClick={onClose}>
              Cancel
            </Button>
            <SubmitButton isLoading={isPending} />
          </DMModalFooter>
        </DMModalContentWrapper>
      </ManagerForm>
    </DMModal>
  );
};
